import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { z } from "zod";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

interface EmailPopupProps {
  triggerOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function EmailPopup({ triggerOpen = false, onOpenChange }: EmailPopupProps) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const hasShown = localStorage.getItem("emailPopupShown");
    
    if (!hasShown) {
      const timer = setTimeout(() => {
        setOpen(true);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (triggerOpen) {
      setOpen(true);
    }
  }, [triggerOpen]);

  const handleDismissPopup = () => {
    setOpen(false);
    onOpenChange?.(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      emailSchema.parse({ email });
      
      const { data, error } = await supabase.functions.invoke('submit-newsletter', {
        body: { email: email.trim(), source: 'popup' }
      });

      if (error) throw error;

      toast({
        title: data.alreadySubscribed ? "Already subscribed!" : "Success! 🎉",
        description: data.message,
      });

      localStorage.setItem("emailPopupShown", "true");
      handleDismissPopup();
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.errors[0].message);
      } else {
        console.error('Newsletter signup error:', err);
        toast({
          title: "Error",
          description: "Failed to subscribe. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDismissPopup}>
      <DialogContent className="sm:max-w-md bg-white text-gray-900 border-0 p-0 gap-0">
        <div className="p-8 text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-2 text-gray-900">
            Save Up To 10%
          </h2>
          <p className="text-xl font-semibold text-gray-600 mb-6">
            YOUR FIRST PURCHASE
          </p>
          <p className="text-base text-gray-700 mb-8 max-w-sm mx-auto">
            Join our community and get exclusive access to the best software deals delivered straight to your inbox!
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                className="w-full h-12 text-base px-4 border-2 border-gray-300 focus:border-[#FDB022]"
                disabled={isSubmitting}
              />
              {error && (
                <p className="text-red-500 text-sm mt-1 text-left">{error}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-[#FDB022] hover:bg-[#e09d1a] text-gray-900 font-bold text-base"
              disabled={isSubmitting}
            >
              {isSubmitting ? "UNLOCKING..." : "UNLOCK MY SAVINGS"}
            </Button>
          </form>

          <button
            onClick={handleDismissPopup}
            className="mt-4 text-sm text-gray-500 hover:text-gray-700 underline"
          >
            No, thanks
          </button>

          <p className="mt-6 text-xs text-gray-400 max-w-sm mx-auto">
            By signing up, you agree to receive marketing emails. Unsubscribe anytime.
            First-time customers only.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
